import React, { useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import '../styles/pages/MultiBox.css';
import Navbar from '../components/layout/Navbar';
import UserNetwork from '../components/multibox/UserNetwork';
import CurrentStateFlow from '../components/assets/MultiBox/current-state-flow.jpg';
import {
  CHAIN, DECISIONS, BOX_TYPES, ONE_PAGER,
  PRD_META, PRD_DELIVERABLE, ASSUMPTIONS, NON_GOALS, OPEN_QUESTIONS,
} from './multiBoxData';
import { JOURNEYS, THRESHOLD_NOTE } from './multiBoxJourneys';

/**
 * Multi-box concept case study.
 *
 * Built on the same scaffolding as Ballys.js — Navbar, a scroll
 * progress rail, a typographic hero, an overview detail grid, then
 * AOS-animated sections — so the case studies read as one family. The
 * Footer comes from App.js, which renders it on every route but home.
 */
const MultiBox = () => {
  const progressRef = useRef(null);
  const progressMobileRef = useRef(null);
  const [journey, setJourney] = useState(JOURNEYS[0].id);

  useEffect(() => {
    AOS.init({ once: false, duration: 700, easing: 'ease-out-cubic' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const percent = docHeight > 0 ? Math.max(0, Math.min(1, scrollTop / docHeight)) : 0;
      if (progressRef.current) progressRef.current.style.height = `${percent * 100}%`;
      if (progressMobileRef.current) progressMobileRef.current.style.width = `${percent * 100}%`;
    };
    window.scrollTo(0, 0);
    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);
    AOS.refresh();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const current = JOURNEYS.find((j) => j.id === journey);
  const sellable = Math.min(...BOX_TYPES.map((b) => b.received));

  return (
    <div className="multibox-page">
      <Navbar />

      <div className="multibox-progress-indicator">
        <div ref={progressRef} style={{ height: 0 }} />
      </div>
      <div className="multibox-progress-indicator-mobile">
        <div ref={progressMobileRef} style={{ width: 0 }} />
      </div>

      {/* ─── Hero ─── */}
      <header className="multibox-hero" data-aos="fade-up">
        <div className="multibox-hero__inner">
          <div className="multibox-hero__eyebrow">Concept case study · October 2026</div>
          <h1 className="multibox-hero__title">Redesigning the supply chain for multi-box&nbsp;products</h1>
          <p className="multibox-hero__subtitle">
            A folding treadmill that ships in three boxes is one thing to the person buying it
            and three things to the warehouse. This is how I’d help a fulfillment network treat
            those boxes as one product you can actually buy, from the first label to the last return.
          </p>
          <div className="multibox-hero__scope">
            <span><strong>Supply chain</strong></span>
            <span className="dot" aria-hidden>·</span>
            <span><strong>Product management</strong></span>
            <span className="dot" aria-hidden>·</span>
            <span><strong>Product design</strong></span>
          </div>
        </div>
      </header>

      <div className="multibox-content">

        {/* PRD "project specifics" — every value restated from the page */}
        <dl className="multibox-prdmeta" data-aos="fade-up">
          {PRD_META.map((m) => (
            <div key={m.label}>
              <dt>{m.label}</dt>
              <dd>{m.value}</dd>
            </div>
          ))}
          <div className="multibox-prdmeta__wide">
            <dt>What I made</dt>
            <dd>{PRD_DELIVERABLE}</dd>
          </div>
        </dl>


        {/* ─── Why ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Context</div>
          <h2>Why I picked this</h2>
          <p>
            Most of my work so far has lived on a screen: sports betting apps, scientific software,
            a date-planning app. Lately I’ve been reading about supply chain, trying to understand
            how product managers and designers can influence an industry that goes way beyond the
            screen and is so tied to real life. So I gave myself one evening and one problem that
            sounds simple: what happens when a single product ships in more than one box?
          </p>
        </section>

        {/* ─── Problem ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Problem</div>
          <h2>The rule that breaks</h2>
          <div className="multibox-callout">
            <h3>The problem</h3>
            <p>
              Fulfillment systems are built on one assumption: one product, one barcode, one package.
              Products that ship in several boxes, like treadmills, sofas and bed frames, break it.
              The customer buys one thing, but the warehouse has to receive, store, ship and sometimes
              take back three.
            </p>
          </div>
          <div className="multibox-callout">
            <h3>Why it matters</h3>
            <p>
              When the system can’t tell those boxes belong together, it shows stock as available when
              a box is missing. Boxes sit stranded in storage, orders arrive incomplete, and returns
              come back that nobody can resell. Sellers feel it as lost sales and disputes, and
              warehouse teams feel it as manual workarounds.
            </p>
          </div>
          <div className="multibox-callout multibox-callout--hmw">
            <h3>Goal</h3>
            <p>
              How might we let a seller set up a multi-box product once, identify every box correctly,
              and have the network treat those boxes as one product you can buy, without changing
              anything for single-box products?
            </p>
          </div>
        </section>

        {/* ─── Chain ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Gap analysis</div>
          <h2>Where it breaks across the chain</h2>
          <p className="multibox-lede">
            I followed one treadmill from the seller’s listing all the way to a customer return.
            Here’s what goes wrong at each step, and the fix I’d try.
          </p>

          <figure className="multibox-figure">
            <img
              src={CurrentStateFlow}
              alt="Six-stage fulfilment flow: multiple suppliers, in transit, receiving at the warehouse, order fulfilment, last mile delivery, delivered and confirmed."
              loading="lazy"
            />
            <figcaption>
              The flow a fulfillment network is built to run, end to end. The six steps below cut
              the same journey a different way — from the seller’s listing through to a customer
              return — so they do not line up one-to-one with the six stages above.
            </figcaption>
          </figure>
          <div className="multibox-table-wrap">
            <table className="multibox-table multibox-chain-table">
              <thead>
                <tr>
                  <th scope="col" className="multibox-chain-table__n">#</th>
                  <th scope="col">Step</th>
                  <th scope="col">What breaks today</th>
                  <th scope="col">The fix I’d try</th>
                </tr>
              </thead>
              <tbody>
                {CHAIN.map((c, i) => (
                  <tr key={c.step}>
                    <td className="multibox-chain-table__n">
                      <span className="multibox-chain__n">{i + 1}</span>
                    </td>
                    <th scope="row">{c.step}</th>
                    <td data-label="What breaks today">{c.breaks}</td>
                    <td className="multibox-chain-table__fix" data-label="The fix I’d try">{c.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="multibox-callout multibox-callout--note">
            <p>
              <strong>What stands out to me:</strong> even the best case keeps each unit’s boxes
              locked together from the factory to the customer. That’s exactly why one missing or
              damaged box strands the others, and why many sellers skip fulfillment networks for
              big items and ship them themselves.
            </p>
          </div>
        </section>

        {/* ─── People ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Users</div>
          <h2>Who’s involved</h2>
          <p className="multibox-lede">
            Seven people touch a multi-box product, and its boxes reach the building by five
            different routes. Each person asks a different question, so each needs a different view
            of the same facts — which is why they all read one carton timeline, inside the tool
            they already use.
          </p>
          <figure className="multibox-figure multibox-figure--scroll">
            <div className="multibox-network-wrap">
              <UserNetwork />
            </div>
            <figcaption>
              Amber: where a product’s boxes come from. Blue: who reads the timeline, and in which
              tool. On a narrow screen the diagram scrolls sideways.
            </figcaption>
          </figure>
        </section>


        {/* ─── Non-goals ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">What we’re not doing</div>
          <h2>Deliberately out of scope</h2>
          <p className="multibox-lede">
            Each of these is a trade-off accepted elsewhere on this page, collected so the
            boundary is in one place rather than spread across the decision log.
          </p>
          <ul className="multibox-nongoals">
            {NON_GOALS.map((n) => (
              <li key={n.text}>
                <strong>{n.text}</strong>
                <span>{n.note}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── Assumptions ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Assumptions</div>
          <h2>What this rests on</h2>
          <p className="multibox-lede">
            If any of these turns out to be wrong, the proposal changes shape rather than
            just losing a feature.
          </p>
          <ol className="multibox-assumptions">
            {ASSUMPTIONS.map((a, i) => (
              <li key={a.text}>
                <span className="multibox-assumptions__n">A{i + 1}</span>
                <div>
                  <p><strong>{a.text}</strong></p>
                  <p className="multibox-assumptions__basis">{a.basis}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* ─── The model ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <h2>How it fits together</h2>
          <p className="multibox-lede">
            Put together, those decisions give the network one simple rule: count boxes by type, and
            sell treadmills. The product stays one listing with one price. Underneath it, every box
            type has its own identity, and the number of treadmills a building can sell is the lowest
            of its box counts.
          </p>
          <div className="multibox-model">
            <div className="multibox-model__product">
              <div className="multibox-eyebrow">Proposed solution</div>
              <h3>Ardent T7 Treadmill</h3>
              <p>One listing, one price, one thing the customer buys.</p>
            </div>
            <div className="multibox-model__types">
              {BOX_TYPES.map((b) => (
                <div className="multibox-model__type" key={b.id}>
                  <span className="multibox-model__id">{b.id}</span>
                  <span className="multibox-model__name">{b.name} · {b.weight}</span>
                  <span className="multibox-model__count">{b.received} received</span>
                </div>
              ))}
            </div>
            <div className="multibox-model__result">
              <div className="multibox-model__big">{sellable}</div>
              <div className="multibox-model__label">sellable treadmills</div>
              <p>
                The lowest count wins. {BOX_TYPES.reduce((n, b) => n + b.received, 0) - sellable * 3} boxes
                stay on hold until 2 more B3s arrive. The listing never shows a treadmill the warehouse
                can’t actually ship, and nobody on the dock has to match boxes into sets by hand.
              </p>
            </div>
          </div>
        </section>

        {/* ─── Decisions ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Decisions and trade-offs</div>
          <h2>The calls I made, and why</h2>
          <p className="multibox-lede">
            Each decision lists what I considered, what I chose, and the trade-off I accepted.
          </p>
          <div className="multibox-decisions">
            {DECISIONS.map((d, i) => (
              <div className="multibox-decision" key={d.question}>
                <div className="multibox-decision__n">{i + 1}</div>
                <div className="multibox-decision__body">
                  <h3>{d.question}</h3>
                  <div className="multibox-decision__options">
                    {d.considered.map((c, ci) => (
                      <span
                        key={c}
                        className={`multibox-option ${ci === d.choseIndex ? 'is-chosen' : ''}`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  <p><strong>Chose.</strong> {d.chose}</p>
                  <p><strong>Why.</strong> {d.why}</p>
                  <p className="multibox-decision__tradeoff"><strong>Trade-off.</strong> {d.tradeoff}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Deep dive ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Requirements · 40 acceptance criteria</div>
          <h2>40 things that can go wrong, and what the platform does</h2>
          <p className="multibox-lede">
            Each row pairs what breaks today with how the platform responds, and who sees it.
          </p>

          <div className="multibox-tabs" role="tablist" aria-label="Journeys">
            {JOURNEYS.map((j) => (
              <button
                key={j.id}
                role="tab"
                type="button"
                id={`mb-tab-${j.id}`}
                aria-selected={journey === j.id}
                aria-controls={`mb-panel-${j.id}`}
                tabIndex={journey === j.id ? 0 : -1}
                className={journey === j.id ? 'is-active' : ''}
                onClick={() => setJourney(j.id)}
              >
                {j.tab} <span className="multibox-tabs__n">{j.count}</span>
              </button>
            ))}
          </div>

          <div
            className="multibox-panel"
            role="tabpanel"
            id={`mb-panel-${current.id}`}
            aria-labelledby={`mb-tab-${current.id}`}
          >
            <p className="multibox-journey-note">{current.note}</p>
            {current.steps.map((step) => (
              <div className="multibox-step" key={step.title}>
                <h3>{step.title}</h3>
                {step.rows.map((r) => (
                  <div className="multibox-case" key={r.n}>
                    <div className="multibox-case__n">{r.n}</div>
                    <div className="multibox-case__cols">
                      <div className="multibox-case__breaks">
                        <h4>What breaks today</h4>
                        <p>{r.breaks}</p>
                        <p className="multibox-case__impact">Impact: {r.impact}</p>
                      </div>
                      <div className="multibox-case__fix">
                        <h4>What the platform does</h4>
                        <p>{r.platform}</p>
                        <div className="multibox-case__who">
                          {r.who.map((w) => <span key={w}>{w}</span>)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="multibox-footnote">{THRESHOLD_NOTE}</p>
        </section>

        {/* ─── One-pager ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Scope and rollout</div>
          <h2>How I’d pilot it</h2>
          <p className="multibox-lede">
            If I were pitching this to a team, this is the page I’d bring into the room.
          </p>
          <div className="multibox-sheet">
            <div className="multibox-sheet__head">
              <span className="multibox-eyebrow">Proposal · Draft</span>
              <h3>Multi-box products: an 8-week pilot</h3>
            </div>
            {ONE_PAGER.map((b) => (
              <div className="multibox-sheet__block" key={b.heading}>
                <h4>{b.heading}</h4>
                {b.body && <p>{b.body}</p>}
                {b.list && <ul>{b.list.map((i) => <li key={i}>{i}</li>)}</ul>}
                {b.split && (
                  <div className="multibox-split">
                    <div>
                      <h5>AI helps</h5>
                      <ul>{b.split.ai.map((i) => <li key={i}>{i}</li>)}</ul>
                    </div>
                    <div>
                      <h5>People decide</h5>
                      <ul>{b.split.people.map((i) => <li key={i}>{i}</li>)}</ul>
                    </div>
                  </div>
                )}
                {b.note && <p className="multibox-footnote">{b.note}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* ─── Next ─── */}
        <section className="multibox-section" data-aos="fade-up">
          <div className="multibox-eyebrow">Open questions</div>
          <h2>What’s next</h2>
          <p className="multibox-lede">
            The calls this proposal deliberately leaves to people, and the things a pilot
            would have to measure before any of the numbers above are settled.
          </p>
          <ul className="multibox-questions">
            {OPEN_QUESTIONS.map((o) => (
              <li key={o.q}>
                <span className={`multibox-questions__kind is-${o.kind.toLowerCase()}`}>{o.kind}</span>
                <span>{o.q}</span>
              </li>
            ))}
          </ul>
          <p>
            I started with the seller’s side because every later step reads the labels it creates.
            The warehouse and customer journeys are mapped here as edge cases, and the next thing I’d
            build is the operations view: waiting sets by age, how full the multi-box zone is, and the
            exceptions queue — the screen that decides whether a pilot like this survives contact with
            a real building.
          </p>
          <p className="multibox-footnote">
            Independent concept project built from public information. It isn’t affiliated with or
            endorsed by any retailer, marketplace or warehouse operator. Product, seller, supplier and
            warehouse names used as examples are made up.
          </p>
        </section>
      </div>
    </div>
  );
};

export default MultiBox;
